import React, { useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { useFetcher } from 'react-router-dom'

import DeleteArchiveWarningDialog from '@components/myScores/ControlArea/DeleteArchiveWarningDialog'
import Button from '@components/shared/Button'
import LoadingSpinner, { SpinnerSize } from '@components/shared/LoadingSpinner'
import { ScoreType } from '@models/Score'
import { getScoreType, isArchived, isPublished, isPublishedScore } from '@models/util/score.util'
import { useSelection } from '@providers/SelectionProvider'
import { useDialogStore } from '@stores/dialog.store'

import type Score from '@models/Score'

const DeleteScoresButton: React.FC = () => {
  const { t } = useTranslation()

  const fetcher = useFetcher()
  const { selectedItems } = useSelection<Score>()
  const openDialog = useDialogStore((s) => s.openDialog)
  const closeDialog = useDialogStore((s) => s.closeDialog)

  /** Do not allow deleting if one of the selected items is published or being sold */
  const hasPublishedOrSelling = useMemo(
    () =>
      selectedItems.some((item) => {
        const scoreType = getScoreType(item)
        return scoreType === ScoreType.PUBLISHED || scoreType === ScoreType.SELLING
      }),
    [selectedItems]
  )

  /**
   * Calculated information about the selected scores to determine if there are published or
   * archived scores present
   */
  const info = useMemo(() => {
    let hasArchived = false
    let hasPublished = false

    selectedItems.forEach((item) => {
      if (isPublishedScore(item)) {
        if (isArchived(item)) hasArchived = true
        else if (isPublished(item)) hasPublished = true
      }
    })

    return { archived: hasArchived, published: hasPublished }
  }, [selectedItems])

  /** Whether the fetcher is submitting or loading data after a delete request */
  const isSubmitting = ['submitting', 'loading'].includes(fetcher.state)

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    if (info.archived || info.published) {
      e.preventDefault()
      openDialog('delete_archive_warning')
      return
    }
  }

  /** Used by the archive warning dialog to send form action post request */
  const submitDelete = (): void => {
    fetcher.submit(
      { scoreIds: selectedItems.map((item) => item.id).join(',') },
      {
        action: '/home/deleteScores',
        method: 'post',
      }
    )
    closeDialog('delete_archive_warning')
  }

  return (
    <>
      <fetcher.Form method="post" action="/home/deleteScores" onSubmit={onSubmit}>
        <Button
          name="scoreIds"
          value={selectedItems.map((item) => item.id)}
          variant="outline"
          type="submit"
          size="sm"
          className="font-medium ml-6 flex flex-row items-center"
          disabled={!selectedItems.length || hasPublishedOrSelling || isSubmitting}>
          {isSubmitting ? (
            <>
              <LoadingSpinner size={SpinnerSize.SMALL} />
              <span className="ml-1">{t('messages.deleting')}</span>
            </>
          ) : (
            <span>{t('action.delete')}</span>
          )}
        </Button>
      </fetcher.Form>
      <DeleteArchiveWarningDialog
        numToDelete={selectedItems.length}
        onDelete={submitDelete}
        hasPublished={info.published}
      />
    </>
  )
}

export default DeleteScoresButton
